import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import clsx from 'clsx'
import { complianceNotes } from '@/utils/patchStage'
import type { AcceptanceResult } from '@/types/patching'
import styles from './Patching.module.css'

function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? 'n/a' : `${value}%`
}

/**
 * The portal's acceptance verdict (contract §11.1). The runner's own numbers are shown only as
 * "reported" — they never decide anything. A refusal always lists its problems.
 */
export function AcceptanceSummary({ acceptance }: { acceptance: AcceptanceResult }) {
  const problems = acceptance.problems ?? []
  const failedMandatory = acceptance.failed_mandatory ?? []
  const notes = complianceNotes(acceptance.compliance_notes)
  const unsupported = acceptance.unsupported_applications ?? []
  const hasReported = acceptance.reported_passed !== undefined && acceptance.reported_passed !== null
  const runnerSaidPassed = acceptance.reported_passed === true && !acceptance.passed

  return (
    <div
      className={clsx(styles.alert, acceptance.passed ? styles.alertInfo : styles.alertError)}
      data-testid="acceptance-summary"
      data-passed={acceptance.passed ? 'true' : 'false'}
    >
      {acceptance.passed ? (
        <CheckCircle2 size={16} className={styles.alertIcon} />
      ) : (
        <AlertTriangle size={16} className={styles.alertIcon} />
      )}
      <div className={styles.stack} style={{ gap: 8 }}>
        <span>
          <strong>Portal acceptance: {acceptance.passed ? 'passed' : 'refused'}.</strong>{' '}
          Compliance (OS patch state, computed by the portal): {pct(acceptance.compliance_pct)}
          {acceptance.threshold_pct !== null && acceptance.threshold_pct !== undefined && ` (threshold ${acceptance.threshold_pct}%)`}.
        </span>

        {problems.length > 0 && (
          <div data-testid="acceptance-problems">
            <strong>Why it was refused</strong>
            <ul className={styles.advisorList}>
              {problems.map((problem, i) => (
                <li key={`${i}-${problem}`}>{problem}</li>
              ))}
            </ul>
          </div>
        )}

        {failedMandatory.length > 0 && <span>Failed mandatory checks: {failedMandatory.join(', ')}.</span>}

        {hasReported && (
          <span data-testid="acceptance-reported" className={styles.muted}>
            Runner reported (informational only): {acceptance.reported_passed ? 'passed' : 'failed'}
            {acceptance.reported_compliance_pct !== null && acceptance.reported_compliance_pct !== undefined
              ? `, ${acceptance.reported_compliance_pct}% compliant`
              : ''}
            .{runnerSaidPassed && ' The runner reported success, but the portal refused acceptance.'}
          </span>
        )}

        {notes.length > 0 && (
          <div data-testid="compliance-notes">
            <strong>Compliance notes</strong>
            <ul className={styles.advisorList}>
              {notes.map((note, i) => (
                <li key={`${i}-${note}`}>{note}</li>
              ))}
            </ul>
          </div>
        )}

        {unsupported.length > 0 && (
          <div data-testid="acceptance-unsupported">
            <span className={styles.chips}>
              <Info size={14} />
              <strong>Applications that are unsupported or unverified</strong>
            </span>
            <span className={styles.hint}> Never counted as compliant. They do not by themselves fail acceptance.</span>
            <ul className={styles.advisorList}>
              {unsupported.map((app, i) => (
                <li key={`${i}-${app.application}`}>
                  {app.vm_name ? <span className={styles.mono}>{app.vm_name} · </span> : null}
                  <span className={styles.mono}>{app.application}</span> ({app.mechanism}) — {app.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
